import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Field } from "@/pages/auth/Login";
import { guestApi, isTransportError, USE_FIXTURES } from "@/lib/api";

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    if (pw.length < 8) return toast.error("Password too short");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      const res = await guestApi.resetPassword({
        email,
        otp,
        password: pw,
        password_confirmation: confirm,
      });
      toast.success(res?.message || "Password updated — please log in.");
    } catch (err) {
      if (USE_FIXTURES && isTransportError(err)) {
        toast.success("Password updated (fixture mode).");
        navigate("/login");
        return;
      }
      toast.error(
        err?.response?.data?.message ||
          err?.apiMessage ||
          err?.message ||
          "Invalid or expired code.",
      );
      setSubmitting(false);
      return;
    }
    navigate("/login");
  };

  return (
    <AuthShell
      eyebrow="Reset password"
      title={<>Choose a new<br /><span className="italic">password</span>.</>}
      subtitle="Enter the 6-digit code from your email and a new password."
    >
      <form onSubmit={onSubmit} className="space-y-6" data-testid="reset-form">
        <Field label="6-digit code" value={otp} onChange={setOtp} testId="reset-otp" />
        <Field
          label="New password"
          type={show ? "text" : "password"}
          value={pw}
          onChange={setPw}
          testId="reset-password"
          rightSlot={
            <button type="button" onClick={() => setShow((v) => !v)}>
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />
        <Field
          label="Confirm password"
          type={show ? "text" : "password"}
          value={confirm}
          onChange={setConfirm}
          testId="reset-confirm"
        />
        <button
          type="submit"
          data-testid="reset-submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60"
        >
          {submitting ? "Saving…" : "Reset password"} <ArrowRight size={16} />
        </button>
        <p className="pt-2 text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline">Back to log in</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
